import React, { useCallback, useEffect, useState } from 'react'
import { Box, Card, CardContent, CardHeader, Stack, Typography } from '@mui/material'
import TimelineIcon from '@mui/icons-material/Timeline'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { oportunidadeService } from '@/services/oportunidadeService'
import { HistoricoEtapa } from '@/types/scoreComercial'
import { ETAPA_COMERCIAL_LABEL, STATUS_LABEL } from '@/types/statusReserva'
import { formatDateTime } from '@/utils/formatters'

interface Props {
  oportunidadeId: string
  /** Traduz o código do motivo de perda em rótulo do catálogo; sem tradutor, mostra o código. */
  rotuloMotivo?: (codigo: string) => string | null
  /** Id do usuário logado — o autor da linha vira "Você" quando é ele. */
  usuarioAtualId?: string | null
}

const setaOu = (de?: string | null, para?: string | null): string | null => {
  if (!de && !para) return null
  if (de === para) return para ?? null
  return `${de ?? '—'} → ${para ?? '—'}`
}

/**
 * Histórico real de etapas (PDF §2.3 / P10): uma linha por transição, lida de
 * `GET /v1/oportunidades/{id}/historico-etapas` — o snapshot que a V47 grava na atividade
 * (status, etapa e score anterior/novo, versão da tabela, motivo de perda, autor).
 * Substitui a lista fixa de dois itens que fingia ser histórico. Estados: carregando,
 * vazio (reserva que nunca mudou de etapa), erro (com tentar novamente).
 */
const HistoricoEtapasCard: React.FC<Props> = ({ oportunidadeId, rotuloMotivo, usuarioAtualId }) => {
  const [linhas, setLinhas] = useState<HistoricoEtapa[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setLinhas(await oportunidadeService.historicoEtapas(oportunidadeId))
    } catch (e) {
      setErro((e as { message?: string })?.message ?? 'Não foi possível carregar o histórico')
    } finally {
      setCarregando(false)
    }
  }, [oportunidadeId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const autorDe = (usuarioId?: string | null): string => {
    if (!usuarioId) return 'autor não registrado'
    if (usuarioAtualId && usuarioId === usuarioAtualId) return 'Você'
    // Sem projeção usuário→nome neste módulo (pendência registrada): o id é o que há.
    return usuarioId
  }

  return (
    <Card data-testid="historico-etapas-card">
      <CardHeader
        title="Histórico de etapas"
        subheader="Cada mudança de etapa, com o score antes e depois — registrado pelo servidor"
      />
      <CardContent>
        {carregando ? (
          <EstadoDeLista estado="carregando" minHeight={80} />
        ) : erro ? (
          <EstadoDeLista
            estado="erro"
            detalhe={erro}
            minHeight={80}
            acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
          />
        ) : linhas.length === 0 ? (
          <EstadoDeLista
            estado="vazio"
            mensagem="Nenhuma mudança de etapa registrada ainda"
            detalhe="A primeira linha aparece quando a reserva avançar (ou for perdida)."
            minHeight={80}
          />
        ) : (
          <Stack spacing={2}>
            {linhas.map((l) => {
              const etapa = setaOu(
                l.etapaAnterior ? ETAPA_COMERCIAL_LABEL[l.etapaAnterior] : null,
                l.etapaNova ? ETAPA_COMERCIAL_LABEL[l.etapaNova] : null,
              )
              const status = setaOu(
                l.statusAnterior ? STATUS_LABEL[l.statusAnterior] : null,
                l.statusNovo ? STATUS_LABEL[l.statusNovo] : null,
              )
              const score =
                l.scoreAnterior != null || l.scoreNovo != null
                  ? setaOu(
                      l.scoreAnterior != null ? String(l.scoreAnterior) : null,
                      l.scoreNovo != null ? String(l.scoreNovo) : null,
                    )
                  : null
              const motivo = l.motivoPerdaCodigo
                ? (rotuloMotivo?.(l.motivoPerdaCodigo) ?? l.motivoPerdaCodigo)
                : null
              return (
                <Box
                  key={l.id}
                  data-testid="historico-etapa-linha"
                  sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <TimelineIcon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {etapa ?? 'Mudança de etapa'}
                    </Typography>
                    {status && (
                      <Typography variant="body2" color="textSecondary">
                        Status: {status}
                      </Typography>
                    )}
                    {score && (
                      <Typography variant="body2" color="textSecondary">
                        Score: {score}
                        {l.versaoScore ? ` (tabela ${l.versaoScore})` : ''}
                      </Typography>
                    )}
                    {motivo && (
                      <Typography variant="body2" color="textSecondary">
                        Motivo da perda: {motivo}
                      </Typography>
                    )}
                    {l.descricao && (
                      <Typography variant="body2" color="textSecondary">
                        {l.descricao}
                      </Typography>
                    )}
                    <Typography variant="caption" color="textSecondary">
                      {formatDateTime(l.createdAt)} · por {autorDe(l.usuarioId)}
                    </Typography>
                  </Box>
                </Box>
              )
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

export default HistoricoEtapasCard
